import {Show} from 'platform/foundation';

import {createPortal} from 'react-dom';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {hasAclDocsWithPermission, usePermissions} from '@dms/shared';
import {DigitalCertificateLink} from '@dms/shared-aftersales';

import {isNotNilOrEmpty, suffixTestId, TestIdProps} from 'shared/web';

import {InspectionIdForAuditId} from '../../../types/InspectionIdForAuditId';
import {AutoFillTemplates} from './AutoFillTemplates';
import {ExportPDF} from './ExportPDF';
import {InspectionActions, InspectionActionType} from './InspectionActions';
import {VerifyCondition} from './VerifyCondition';

const HIDDEN_ACTIONS = ['delete', 'clone'] as const satisfies InspectionActionType[];

interface ConditionHeaderPortalProps extends TestIdProps, Omit<InspectionIdForAuditId, 'auditId'> {
  vehicleId?: string;
  inspectionId?: string;
  portalTarget: HTMLDivElement | null;
}

export function ConditionHeaderPortal(props: ConditionHeaderPortalProps) {
  const {data: vehicle} = useGetVehicleQuery(
    {vehicleId: props.vehicleId ?? ''},
    {skip: isNilOrEmpty(props.vehicleId)}
  );
  const {data: vehicleParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: props.vehicleId ?? '',
    },
    {skip: isNilOrEmpty(props.vehicleId)}
  );

  const [hasReadVehicleConditionDocumentPermission] = usePermissions({
    permissionKeys: ['readVehicleConditionDocument'],
    scopes: {
      readVehicleConditionDocument: {
        branchId: vehicle?.branchId,
        participation: vehicleParticipation,
      },
    },
  });

  if (isNil(props.portalTarget)) {
    return null;
  }

  return createPortal(
    <>
      <Show
        when={
          isNotNilOrEmpty(props.inspectionId) && isNotNilOrEmpty(props.vehicleId)
            ? {inspectionId: props.inspectionId, vehicleId: props.vehicleId}
            : null
        }
      >
        {(inspection) => (
          <InspectionActions
            inspectionId={inspection.inspectionId}
            vehicleId={inspection.vehicleId}
            inspectionType={null}
            hideActions={HIDDEN_ACTIONS}
          />
        )}
      </Show>
      <DigitalCertificateLink />
      <ExportPDF
        data-testid={suffixTestId('exportPdf', props)}
        canExport={hasAclDocsWithPermission(hasReadVehicleConditionDocumentPermission)}
      />
      <AutoFillTemplates
        data-testid={suffixTestId('conditionHeader', props)}
        shouldUseAuditIdForUpdateTemplate={props.shouldUseAuditIdForUpdateTemplate}
      />
      <VerifyCondition data-testid={suffixTestId('conditionHeader', props)} />
    </>,
    props.portalTarget
  );
}
